export interface ILogger {
  start: (name: string, params?: Record<string, unknown>) => void;
  end: (name: string, params?: Record<string, unknown>) => void;
  log: (name: string, params?: Record<string, unknown>) => void;
  error: (name: string, params?: Record<string, unknown>) => void;
  verbose: (name: string, params?: Record<string, unknown>) => void;
}

const VERBOSE = false;
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Safely converts params to string for logging without OOMing the Capacitor bridge.
 * Truncates large strings (like base64 audio/images) and describes Blobs.
 */
function safeStringify(params?: Record<string, unknown>): string {
  if (!params) return '';
  try {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'string' && value.length > 1000) {
        sanitized[key] = `[Large String: ${value.length} chars - Truncated]`;
      } else if (value instanceof Blob) {
        sanitized[key] = `[Blob: ${value.size} bytes]`;
      } else if (value instanceof ArrayBuffer || ArrayBuffer.isView(value)) {
        sanitized[key] = `[Buffer: ${'byteLength' in value ? value.byteLength : 'unknown'} bytes]`;
      } else if (Array.isArray(value) && value.length > 50) {
        sanitized[key] = `[Large Array: ${value.length} items]`;
      } else {
        sanitized[key] = value;
      }
    }
    return ` – ${JSON.stringify(sanitized)}`;
  } catch {
    return ' – [Unserializable Object]';
  }
}

export class Logger implements ILogger {
  start(name: string, params?: Record<string, unknown>): void {
    if (IS_PRODUCTION) return;
    console.log(`${name}:START${safeStringify(params)}`);
  }

  end(name: string, params?: Record<string, unknown>): void {
    if (IS_PRODUCTION) return;
    console.log(`${name}:END${safeStringify(params)}`);
  }

  log(name: string, params?: Record<string, unknown>): void {
    if (IS_PRODUCTION) return;
    console.log(`${name}:LOG${safeStringify(params)}`);
  }

  error(name: string, params?: Record<string, unknown>): void {
    // Keep errors even in production for analytics/Sentry, but SANITIZE them.
    console.error(`${name}:ERROR${safeStringify(params)}`);
  }

  verbose(name: string, params?: Record<string, unknown>): void {
    if (VERBOSE && !IS_PRODUCTION) {
      console.log(`${name}:VERBOSE${safeStringify(params)}`);
    }
  }
}

export const tlLogger = new Logger();
